import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorMessage, Form, Formik } from 'formik';
import {
  Col,
  Row,
  Container,
  OverlayTrigger,
  Card,
  Spinner,
} from 'react-bootstrap';
import Image1 from '../../assets/Images/Onboarding/First.webp';
import Image2 from '../../assets/Images/Onboarding/Second.webp';
import Image4 from '../../assets/Images/Onboarding/Fourth.webp';
import Image6 from '../../assets/Images/Onboarding/Sixth.webp';
import styled, { css } from 'styled-components';
import ArrowRight from '../../app/components/svgIcons/ArrowRight';
import InfoIcon from '../../app/components/svgIcons/InfoIcon';
import FormikIconInput from '../../app/components/formikComponents/FormikIconInput';
import {
  useGetFloralServicesQuery,
  useUpdateFloriestDetailsMutation,
} from '../shopSetting/shopAPISlice';
import StyledText from '../../app/components/styledComponents/StyledText';
import StyledThemeButton from '../../app/components/styledComponents/StyledButton';
import { StyledTooltip } from '../../app/components/styledComponents/StyledTooltip';
import { toast } from 'react-toastify';
import {
  onboardingSchemaStepOne,
  onboardingSchemaStepTwo,
} from '../shopSetting/assets/shopUtils';
import FormikAsyncMap from '../../app/components/formikComponents/FormikAsyncMap';
import FormikCountryInput from '../../app/components/formikComponents/FormikCountryInput';
import StyledCheckbox from '../../app/components/checkbox/StyledCheckbox';
import {
  useGetTemplateSelectedDataQuery,
  useGetTemplatedataQuery,
  useUpdateTemplateDataMutation,
} from '../vendors/vendorsApiSlice';
import EyeIcon from '../../app/components/svgIcons/EyeIcon';
import CheckArrow from '../settings/EditAndPreviewTemplate/assets/checkArrow';
import {
  useAddCatalogueMutation,
  useGetProductsQuery,
} from '../products/productsApiSlice';
import ProductListViewCard from '../products/ProductListViewCard';
import { useGetUserDetailsQuery } from '../profile/profileApiSlice';
import useAuth from '../../hooks/useAuth';
import { formErrorMessage } from '../../app/components/formikComponents/FormErrorMessage';
import defaultIcon from '../../assets/Images/logoIcon.jpg';
import { Offcanvas } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { updateCredentials } from '../auth/authSlice';

const initialValuesStepOne = {
  companyName: '',
  address: '',
  contactNo: '',
  contactUsEmail: '',
};

const initialValuesStepTwo = {
  floralServicesList: [],
};

function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: templateData, isLoading: loadTemplate } =
    useGetTemplatedataQuery();
  const [createNewTemplate, { isLoading: addLoading }] =
    useUpdateTemplateDataMutation();
  const { data: initialUserDetails, isLoading: fetchingUser } =
    useGetUserDetailsQuery({ forceRefetch: true });
  const { data: templateSelectedData, refetch: getTemplateData } =
    useGetTemplateSelectedDataQuery(null, { refetchOnMountOrArgChange: true });

  const { data: floralServices } = useGetFloralServicesQuery();
  const [updateFlorist, { isLoading: updatingFlorist }] =
    useUpdateFloriestDetailsMutation();
  const [addCatalogueAPI, { isLoading: addingProducts }] =
    useAddCatalogueMutation();
  const { data, refetch: refetchProducts } = useGetProductsQuery(
    {
      size: 50,
      page: 0,
    },
    {
      forceRefetch: true,
      refetchOnMountOrArgChange: true,
    },
  );
  const [step, setStep] = useState(0);
  const [userDetails, setUserDetails] = useState(initialUserDetails);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [shopInfo, setShopInfo] = useState({});
  const [icon, setIcon] = useState(null);

  const [showRightPreview, setShowRightPreview] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(null);
  const [currentSelectedTemplate, setCurrentSelectedTemplate] = useState({});
  const [loadingNextStep , setLoadingNextStep] = useState(false);


  useEffect(() => {
    setUserDetails(initialUserDetails);
  }, [initialUserDetails]);

  useEffect(() => {
    setCurrentSelectedTemplate(templateSelectedData);
  }, [templateSelectedData]);

  const handleChangeStep = () => {
    setStep(3);
  };

  const handleStepChange = async () => {
    if (step === 3) {
      if (!currentSelectedTemplate?.templateId) {
        setLoadingNextStep(false);
        return toast.error('Please select a template');
      } else {
        await createTemplate();
      }
    }
    setStep((prev) => prev + 1);
    setLoadingNextStep(false);
  };

  const handleSubmitFormStepOne = (values) => {
    setShopInfo(values);
    handleStepChange();
  };

  const handleSubmitFormStepTwo = async (values) => {
    try {
      const payload = { ...values, ...shopInfo };
      setShopInfo(payload);
      const res = await updateFlorist({
        ...payload,
        imageDTO: icon,
        id: userDetails?.floristDTO?.id,
        userId: userDetails?.floristDTO?.userId,
      });
      if (res && res?.data) {
        toast.success(
          res?.data.message || 'Shop details Updated Successfully!',
        );
        setUserDetails(res?.data?.objectData);
        dispatch(updateCredentials({ floristDTO: res?.data?.objectData }));
      }
      handleStepChange();
    } catch (error) {}
  };

  const handleRedirect = () => {
    navigate('/dashboard');
  };

  const createTemplate = async () => {
    try {
      if (!currentSelectedTemplate?.templateId) {
        toast.error('Something went wrong');
        return;
      }

      const { data } = await createNewTemplate({
        id: currentSelectedTemplate.id,
        templateId: currentSelectedTemplate.templateId,
        floristId: currentSelectedTemplate.floristId,
      });
      if (!data?.error) {
        getTemplateData();
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const onQuickAdd = async (id) => {
    try {
      const { data } = await addCatalogueAPI([id]);
      if (!data?.error) {
        toast.success(
          data?.description || 'Inventory Catalogue Saved Successfully',
        );
        refetchProducts();
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleAddMultipleProducts = async () => {
    const { data } = await addCatalogueAPI(
      selectedProducts?.map((item) => ({ id: item })) || [],
    );
    if (!data?.error) {
      toast.success(
        data?.description || 'Inventory Catalogue Saved Successfully',
      );
      refetchProducts();
    }
  };

  const handleProductSelection = (productId, isSelected) => {
    setSelectedProducts((prevProducts) =>
      isSelected
        ? [...prevProducts, productId]
        : prevProducts.filter((id) => id !== productId),
    );
  };

  useEffect(() => {
    const getDefaultShopIcon = async () => {
      try {
        const response = await fetch(defaultIcon);
        const blob = await response.blob();

        const reader = new FileReader();
        reader.onload = () => {
          const base64Data = reader.result;
          const payload = {
            content: base64Data,
            fileName: defaultIcon.substring(defaultIcon.lastIndexOf('/') + 1),
            extension: blob.type,
          };
          setIcon(payload);
        };
        reader.readAsDataURL(blob);
      } catch (error) {}
    };

    getDefaultShopIcon();
  }, []);

  const renderInput = ({
    label,
    toolTip,
    component,
    isRequired,
    parentClass = '',
  }) => (
    <div className={`mb-3 ${parentClass ?? ''} w-100`}>
      <FormLabel>
        <span className={`title ${isRequired ? 'required' : ''}`}>{label}</span>
        <OverlayTrigger
          overlay={<StyledTooltip>{toolTip || label}</StyledTooltip>}
        >
          <span className="cursor-pointer ms-3 ">
            <InfoIcon className="mb-1" />
          </span>
        </OverlayTrigger>
      </FormLabel>
      {component}
    </div>
  );

  return (
    <Wrapper>
      <Container fluid className="p-0">
        {step === 0 && (
          <Row className="m-0 vh-100">
            <Col className="p-0 h-100">
              <img loading="lazy" src={Image1} alt="" className="h-100 w-100" />
            </Col>
            <Col className="p-0 h-100">
              <WelcomeContent className="d-flex flex-column justify-content-center gap-4 h-100">
                <StyledText as="h1" color="white" $fontSize="32px">
                  Welcome {user?.email}
                </StyledText>

                <StyledText as="p" color="white" $fontSize="22px">
                  We’re excited to help you create an amazing Flower Shop
                  website.
                  <br />
                  Let’s get started setting up your profile.
                </StyledText>

                <div>
                  <StyledButton
                    className="py-2 px-3"
                    type="button"
                    color="#ffffff"
                    onClick={() => handleStepChange()}
                  >
                    <StyledText as="p" color="black" $fontSize="16px">
                      Let's do this <ArrowRight />
                    </StyledText>
                  </StyledButton>
                </div>
              </WelcomeContent>
            </Col>
          </Row>
        )}

        {step === 1 && (
          <Formik
            enableReinitialize
            initialValues={initialValuesStepOne}
            validationSchema={onboardingSchemaStepOne}
            validateOnMount
            validateOnChange
            onSubmit={handleSubmitFormStepOne}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Row className="m-0 vh-100">
                  <Col className="p-0  h-100">
                    <img
                      loading="lazy"
                      src={Image2}
                      alt=""
                      className="h-100 w-100"
                    />
                  </Col>
                  <Col className="p-0  h-100 position-relative">
                    <ContentWrapper className="d-flex flex-column justify-content-center gap-2 h-100">
                      <div className="w-75 align-self-center">
                        <StyledText
                          as="h1"
                          color="black"
                          $fontSize="32px"
                          className="mb-4"
                        >
                          Tell us about your Shop
                        </StyledText>

                        {renderInput({
                          label: 'Shop Name',
                          toolTip: 'Shop Name',
                          isRequired: false,
                          component: (
                            <FormikIconInput
                              name="companyName"
                              id="companyName"
                              placeholder="Enter Company Name"
                              showErrorOnMount
                              autoComplete="nope"
                            />
                          ),
                        })}

                        {renderInput({
                          label: 'Address',
                          toolTip: 'Address',
                          isRequired: false,
                          component: (
                            <FormikAsyncMap
                              name="address"
                              $fontSize="16px"
                              $phFontSize="16px"
                              placeholder="Enter address"
                            />
                          ),
                        })}

                        {renderInput({
                          label: 'Shop Email',
                          toolTip:
                            'This is your business email where order confirmation is sent',
                          isRequired: false,
                          component: (
                            <FormikIconInput
                              name="contactUsEmail"
                              id="contactUsEmail"
                              placeholder="info@flowershop.com"
                              autoComplete="nope"
                            />
                          ),
                        })}

                        {renderInput({
                          label: 'Phone Number',
                          toolTip: 'Phone Number',
                          isRequired: false,
                          component: (
                            <FormikCountryInput
                              name="contactNo"
                              id="contactNo"
                              placeholder="(+1) 123  _ _ _   _ _ _ _ "
                              autoComplete="aadfdfb"
                            />
                          ),
                        })}
                      </div>
                    </ContentWrapper>
                    <Footer>
                      <StyledThemeButton
                        className="py-2 px-3"
                        $fontWeight={'500'}
                        $fontSize={'18px'}
                        type="submit"
                      >
                        Next
                      </StyledThemeButton>
                    </Footer>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}

        {step === 2 && (
          <Formik
            enableReinitialize
            initialValues={initialValuesStepTwo}
            validationSchema={onboardingSchemaStepTwo}
            validateOnMount
            validateOnChange
            onSubmit={handleSubmitFormStepTwo}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Row className="m-0 vh-100">
                  <Col className="p-0 h-100">
                    <img
                      loading="lazy"
                      src={Image2}
                      alt=""
                      className="h-100 w-100"
                    />
                  </Col>
                  <Col className="p-0 h-100 position-relative">
                    <ContentWrapper className="d-flex flex-column justify-content-center gap-2 h-100">
                      <div className="w-75 align-self-center">
                        <StyledText
                          as="h1"
                          color="black"
                          $fontSize="32px"
                          className="mb-4"
                        >
                          Which wire services do you currently use?
                        </StyledText>

                        <StyledText
                          as="h1"
                          color="black"
                          $fontSize="22px"
                          className="mb-4"
                        >
                          Don't worry, we are not a wire service. This
                          information is used to populate the directory of
                          Florists.
                        </StyledText>

                        {floralServices?.objectData?.length ? (
                          <ul>
                            {floralServices.objectData.map((item, index) => (
                              <li key={index} className="mb-4">
                                <StyledText
                                  as="h1"
                                  color="black"
                                  $fontSize="18px"
                                >
                                  <RoundedStyledCheckout
                                    value={values.floralServicesList.some(
                                      (i) => i.id === item.id,
                                    )}
                                    label={item.name}
                                    rounded
                                    borderColor={'bodyMedium'}
                                    onChange={(event) => {
                                      const selectedItem = item;

                                      if (selectedItem.name === 'None') {
                                        setFieldValue('floralServicesList', [
                                          selectedItem,
                                        ]);
                                      } else {
                                        const updatedList =
                                          values.floralServicesList.filter(
                                            (i) => i.name !== 'None',
                                          );

                                        if (event.target.checked) {
                                          updatedList.push(selectedItem);
                                        } else {
                                          const selectedIndex =
                                            updatedList.findIndex(
                                              (i) => i.id === selectedItem.id,
                                            );
                                          if (selectedIndex !== -1) {
                                            updatedList.splice(
                                              selectedIndex,
                                              1,
                                            );
                                          }
                                        }

                                        setFieldValue(
                                          'floralServicesList',
                                          updatedList,
                                        );
                                      }
                                    }}
                                  />
                                </StyledText>
                              </li>
                            ))}

                            <ErrorMessage
                              name={'floralServicesList'}
                              render={formErrorMessage}
                            />
                          </ul>
                        ) : (
                          <StyledText
                            as="h1"
                            color="black"
                            $fontSize="22px"
                            className="mb-4"
                          >
                            No services available
                          </StyledText>
                        )}
                      </div>
                    </ContentWrapper>
                    <Footer>
                      <StyledThemeButton
                        className="py-2 px-3 d-flex gap-2"
                        $fontWeight={'500'}
                        $fontSize={'18px'}
                        type="submit"
                        loading={fetchingUser || updatingFlorist}
                        disabled={fetchingUser || updatingFlorist}
                      >
                        
                        <span>Next</span>
                      </StyledThemeButton>
                    </Footer>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        )}

        {step === 3 && (
          <Row className="m-0 vh-100">
            <Col className="p-0 h-100">
              <img loading="lazy" src={Image4} alt="" className="h-100 w-100" />
            </Col>
            <Col className="p-0 h-100 position-relative d-flex flex-column">
              <ContentWrapper className="d-flex flex-column justify-content-center gap-2 pb-0 mb-4">
                <StyledText
                  as="h1"
                  color="black"
                  $fontSize="32px"
                  className="mb-2"
                >
                  Now time for the fun part!
                </StyledText>

                <StyledText as="p" color="black" $fontSize="22px">
                  Choose one of these templates as the starting point for your
                  website. Don’t worry, you can change it later.
                </StyledText>
              </ContentWrapper>

              <ThemesContainer className="px-5">
                <Grid id="TemplateContainer">
                  {templateData?.map((card, index) => (
                    <StyledCard key={index} id={index}>
                      <CardImage>
                        <Card.Img src={card?.imageUrl} />
                        {currentSelectedTemplate?.templateId ===
                          card.templateId && (
                          <SelectedBg>
                            <Round1>
                              <Round2>
                                <CheckArrow />
                              </Round2>
                            </Round1>
                          </SelectedBg>
                        )}
                      </CardImage>

                      <Card.Body
                        className="px-0 pb-2"
                        id={`card-body-${index}`}
                      >
                        <BottomContainer>
                          <HeadingText>{card?.name}</HeadingText>

                          {currentSelectedTemplate?.templateId ===
                            card.templateId && (
                            <ButtonContainer>
                              <SelectButton className="px-2">
                                Selected
                              </SelectButton>
                            </ButtonContainer>
                          )}

                          {currentSelectedTemplate?.templateId !==
                            card.templateId && (
                            <ButtonContainer>
                              <ViewButton
                                type="button"
                                onClick={() => {
                                  setPreviewTheme(card);
                                  setShowRightPreview(true);
                                }}
                              >
                                <EyeIcon className="me-1" />
                                View
                              </ViewButton>
                              {/* </a> */}
                              <SelectButton
                                className="px-2"
                                type="button"
                                onClick={() => setCurrentSelectedTemplate(card)}
                              >
                                Select
                              </SelectButton>
                            </ButtonContainer>
                          )}
                        </BottomContainer>
                      </Card.Body>
                    </StyledCard>
                  ))}
                </Grid>

                {(loadTemplate || addLoading) && (
                  <div className="position-absolute top-0 bottom-0 start-0 end-0 d-flex align-items-center justify-content-center bg-white bg-opacity-50">
                    <Spinner animation="border" variant="primary" />
                  </div>
                )}
              </ThemesContainer>

              <Footer>
                <StyledThemeButton
                  className="py-2 px-3"
                  $fontWeight={'500'}
                  $fontSize={'18px'}
                  type="button"
                  // loading={loadingNextStep}
                  disabled={loadingNextStep}
                  onClick={() => {
                    setLoadingNextStep(true);
                    handleStepChange()
                  }}
                >
                  Next
                </StyledThemeButton>
              </Footer>
            </Col>
          </Row>
        )}

        {step === 4 && (
          <Row className="m-0 vh-100">
            <Col className="p-0 h-100">
              <img loading="lazy" src={Image6} alt="" className="h-100 w-100" />
            </Col>
            <Col className="p-0 h-100 position-relative">
              <div className="d-flex align-items-center h-100">
                <ContentWrapper2 className="d-flex flex-column justify-content-center">
                  <div className="w-75 align-self-center">
                    <StyledText
                      as="h1"
                      color="black"
                      $fontSize="32px"
                      className="mb-2"
                    >
                      Congratulations!
                    </StyledText>

                    <StyledText as="p" color="black" $fontSize="22px">
                      Your sandbox website is now available! You have unlimited
                      options to customize your site to make it uniquely yours.
                    </StyledText>

                    <div className="mt-5">
                      {currentSelectedTemplate &&
                        currentSelectedTemplate?.name && (
                          <div>
                            <TopInfoBox>
                              <SelectedImage>
                                <Card.Img
                                  src={currentSelectedTemplate?.imageUrl}
                                />
                              </SelectedImage>
                              <ThemeInfoContainer>
                                <div>
                                  <StyledText
                                    as="h5"
                                    $fontSize="26px"
                                    color="heading"
                                    $fontWeight="600"
                                  >
                                    Web Template:{' '}
                                    {currentSelectedTemplate?.name}
                                  </StyledText>
                                </div>
                              </ThemeInfoContainer>
                            </TopInfoBox>
                          </div>
                        )}
                    </div>
                  </div>
                </ContentWrapper2>
                <Footer>
                  <StyledThemeButton
                    className="py-2 px-3"
                    $fontWeight={'500'}
                    $fontSize={'18px'}
                    type="button"
                    onClick={handleRedirect}
                  >
                    Finish
                  </StyledThemeButton>
                </Footer>
              </div>
            </Col>
          </Row>
        )}
      </Container>

      <Offcanvas
        show={showRightPreview}
        onHide={() => setShowRightPreview(false)}
        placement={'end'}
        style={{ width: '90%' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{previewTheme?.name}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <iframe
            title="template-previews"
            src={previewTheme?.redirectUrl + '?disableCheckout=true'}
            height={'100%'}
            width={'100%'}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  // font-family: Instrument Sans;

  h1 {
    margin: 0px;
  }

  p {
    margin: 0px;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }
`;

const WelcomeContent = styled.div`
  background-color: #170361;
  padding: 5rem;
`;

const ContentWrapper = styled.div`
  padding: 3rem;
`;

const ContentWrapper2 = styled.div`
  padding: 2rem 3rem;
`;

const StyledButton = styled.button`
  background-color: ${({ color }) => color ?? '#000000'};
  border: none;
  border-radius: 20px;
`;

const ProductsContainer = styled.div`
  max-height: 62vh;
  padding: 2rem 0;
  overflow: auto;
`;

const ProductGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(721px, 1fr));
  ${({ theme }) => css`
    @media (max-width: ${theme?.breakpoints?.md}px) {
      grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
    }
  `}
  gap: 30px;
`;

const FormLabel = styled.div`
  color: ${({ theme }) => theme.heading};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  margin-bottom: 5px;
`;

const StyledCard = styled(Card)`
  margin: 0;
  border: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);
  }
`;

const CardImage = styled.div`
   position: relative;
   height: 200px;
   img {
     height: 100%;
     width: 100%
     object-fit: cover;
   }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr); /* Two columns with equal width */
  grid-gap: 1rem;
`;

const BottomContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0px 16px 5px;
  flex-wrap: wrap;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  height: 48px;
`;

const HeadingText = styled.div`
  color: ${({ theme }) => theme.heading};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`;

const ViewButton = styled.div`
  display: flex;
  padding: 9.5px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.16),
    0px 0px 4px 0px rgba(0, 0, 0, 0.16);
  color: ${({ theme }) => theme.heading};
  text-align: center;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 160%;
  height: 48px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.lightHover};
  }
`;

const SelectButton = styled(StyledButton)`
  color: #fff;
  text-align: center;
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: 16px;
  font-style: normal;
  font-weight: 6400;
  line-height: 160%;
  height: 48px;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.16),
    0px 0px 4px 0px rgba(0, 0, 0, 0.16);
`;

const SelectedBg = styled.div`
  top: 0;
  position: absolute;
  border-radius: 8px;
  background: linear-gradient(
    270deg,
    rgba(0, 0, 0, 0.56) 0%,
    rgba(0, 0, 0, 0.56) 100%
  );
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.06),
    0px 0px 4px 0px rgba(0, 0, 0, 0.04);
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const Round1 = styled.div`
  width: 130px;
  height: 130px;
  flex-shrink: 0;
  border-radius: 100px;
  border: 8px solid #fff;
  justify-content: center;
  align-items: center;
  display: flex;
`;

const Round2 = styled.div`
  width: 100px;
  height: 100px;
  flex-shrink: 0;
  border-radius: 100px;
  background: #fff;
  justify-content: center;
  align-items: center;
  display: flex;
  color: ${({ theme }) => theme.primary};
`;

const TopInfoBox = styled(Row)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.08),
    0px 0px 4px 0px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  padding: 20px;
  margin-left: 0px;
  gap: 1rem;
`;

const SelectedImage = styled(Col)`
  max-width: 80%;
  border-radius: 8px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.06),
    0px 0px 4px 0px rgba(0, 0, 0, 0.04);
  img {
    object-fit: cover;
  }
`;

const ThemeInfoContainer = styled(Col)`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 50px;
  max-width: 80%;
`;

const RoundedStyledCheckout = styled(StyledCheckbox)`
  height: 22px;
  width: 25px;
  box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.1);
`;

const Footer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  display: flex;
  justify-content: flex-end;
  border-radius: 0px 0px 8px 8px;
  border-top: 1px solid ${({ theme }) => theme.dark70};
  background: #fff;
  box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.08),
    0px 0px 6px 0px rgba(0, 0, 0, 0.02);
  padding: 1rem;
`;

const ThemesContainer = styled.div`
  overflow: auto;
  padding-top: 25px;
  padding-bottom: 100px;
`;

export default Onboarding;
